import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"

import {
    Box,
    Typography,
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Switch,
    Tooltip,
} from "@mui/material"

import {
    MonitorHeartOutlined as MonitorHeartOutlinedIcon,
    QueryStatsOutlined as QueryStatsOutlinedIcon,
    DesktopWindowsOutlined as DesktopWindowsOutlinedIcon,
    AutoAwesomeOutlined as AutoAwesomeOutlinedIcon,
    BarChartOutlined as BarChartOutlinedIcon,
    Security as SecurityIcon,
    FactCheckOutlined as FactCheckOutlinedIcon,
    LinkRounded as LinkRoundedIcon,
    AccessTime as AccessTimeIcon,
    GroupOutlined as GroupOutlinedIcon,
    TrendingUp as TrendingUpIcon,
    ArrowForward as ArrowForwardIcon,
    Check as CheckIcon,
    ChatBubbleOutline as ChatBubbleOutlineIcon,
    WhatsApp as WhatsAppIcon,
    VisibilityOff as VisibilityOffIcon,
    Gavel as GavelIcon,
    MonetizationOn as MonetizationOnIcon,
    BoltOutlined as BoltOutlinedIcon,
    AdjustOutlined as AdjustOutlinedIcon,
    CloseOutlined as CloseOutlinedIcon,
    LockOutlined as LockOutlinedIcon,
    NotificationsActiveOutlined as NotificationsActiveOutlinedIcon,
    // StarRounded as StarRoundedIcon,
    PersonAddAlt1Outlined as PersonAddAlt1OutlinedIcon,
    SmartphoneOutlined as SmartphoneOutlinedIcon,
    KeyboardArrowDownRounded as KeyboardArrowDownRoundedIcon,
    KeyboardArrowUpRounded as KeyboardArrowUpRoundedIcon,
    Lock as LockIcon,
    Discount as DiscountIcon,
} from "@mui/icons-material"
import LandingFooter from "../components/LandingFooter"
import LandingButton from "../components/landing/LandingButton"
import LandingFeatureListItem from "../components/landing/LandingFeatureListItem"
import LandingPill from "../components/landing/LandingPill"
import LandingProblemCard from "../components/landing/LandingProblemCard"

import MoneyOffIcon from "@mui/icons-material/MoneyOff"
import GroupsIcon from "@mui/icons-material/Groups"
import BusinessIcon from "@mui/icons-material/Business"

import { scheduleDemo } from "../services/utils"
import { listStripePlans } from "../services/scout"

const faqItems = [
    {
        question: "O Kwik Scout acessa o conteúdo das mensagens?",
        answer: "Não. Apenas registramos metadados e estatísticas para gerar relatórios e insights. As mensagens não são lidas por nossa equipe."
    },
    {
        question: "É necessário instalar algo no celular dos atendentes?",
        answer: "Não. A conexão é feita via leitura de QR code no nosso painel."
    },
    {
        question: "Os dados são armazenados de forma segura?",
        answer: "Sim. Seguimos padrões de segurança e proteção conforme exigido pela LGPD."
    },
    {
        question: "Posso exportar os relatórios?",
        answer: "Sim, é possível baixar relatórios em PDF ou ZIP."
    },
    {
        question: "Qual o período mínimo de contratação?",
        answer: "Você pode iniciar pelo período de teste de 7 dias e, posteriormente, escolher o plano mensal."
    },
    {
        question: "Como funciona o teste grátis?",
        answer: "Você ativa o período de teste em poucos minutos e pode explorar os principais recursos da plataforma antes de contratar um plano."
    },
    {
        question: "Vocês oferecem suporte em português?",
        answer: "Sim. Nosso time atende em português e acompanha toda a etapa inicial de configuração e uso da plataforma."
    },
]

const heroBenefits = [
    "Setup em 5 minutos",
    "Sem cartão de crédito",
    "Cancele quando quiser",
]

const vulnerabilityItems = [
    {
        title: "Falta de visibilidade",
        description: "Você não sabe o que está sendo dito aos seus clientes no WhatsApp.",
        Icon: VisibilityOffIcon,
    },
    {
        title: "Risco de compliance",
        description: "Conversas sem registro podem gerar problemas jurídicos e regulatórios.",
        Icon: GavelIcon,
    },
    {
        title: "Atendimento inconsistente",
        description: "Cada atendente comunica de um jeito diferente, fragilizando toda a experiência.",
        Icon: ChatBubbleOutlineIcon,
    },
    {
        title: "Perda de receita",
        description: "Oportunidades perdidas por falta de acompanhamento das conversas.",
        Icon: MonetizationOnIcon,
    },
]

const platformFeatures = [
    {
        title: "Painel de monitoria em tempo real",
        description: "Visualize todas as conversas ativas da sua equipe em um dashboard centralizado.",
        Icon: DesktopWindowsOutlinedIcon,
    },
    {
        title: "Auditoria de conversas",
        description: "Acesse o histórico completo de qualquer conversa para revisão e treinamento.",
        Icon: ChatBubbleOutlineIcon,
    },
    {
        title: "Resumos inteligentes com IA",
        description: "Utilize IA para gerar resumos automáticos das conversas do WhatsApp e economize tempo na análise.",
        Icon: AutoAwesomeOutlinedIcon,
    },
    {
        title: "Relatórios e insights",
        description: "Gere relatórios detalhados de performance e qualidade do atendimento.",
        Icon: BarChartOutlinedIcon,
    },
]

const resultsItems = [
    {
        title: "Economia de tempo",
        description: "Reduza horas de supervisão manual com alertas automáticos.",
        Icon: AccessTimeIcon,
    },
    {
        title: "Mais controle",
        description: "Saiba exatamente o que está sendo comunicado aos seus clientes.",
        Icon: AdjustOutlinedIcon,
    },
    {
        title: "Equipe alinhada",
        description: "Garanta que todos seguem o padrão de atendimento da empresa.",
        Icon: GroupOutlinedIcon,
    },
    {
        title: "Aumento de receita",
        description: "Identifique oportunidades perdidas e melhore a conversão.",
        Icon: TrendingUpIcon,
    },
    {
        title: "Conformidade legal",
        description: "Mantenha registros auditáveis para compliance e LGPD.",
        Icon: SecurityIcon,
    },
    {
        title: "Decisões rápidas",
        description: "Dashboards em tempo real para agir no momento certo.",
        Icon: BoltOutlinedIcon,
    },
]

const comparisonColumns = [
    {
        title: "Sem Kwik Scout",
        tone: "negative",
        Icon: CloseOutlinedIcon,
        items: [
            "Conversas sem registro ou auditoria",
            "Sem visibilidade do atendimento",
            "Riscos de compliance e LGPD",
            "Treinamento baseado em achismo",
            "Oportunidades perdidas sem saber",
            "Gestão reativa e lenta",
        ],
    },
    {
        title: "Com Kwik Scout",
        tone: "positive",
        Icon: CheckIcon,
        items: [
            "Histórico completo e auditável",
            "Dashboard em tempo real",
            "Conformidade total e registros seguros",
            "Insights baseados em dados reais",
            "Alertas automáticos de oportunidades",
            "Decisões proativas e em tempo real",
        ],
    },
]

const featureCards = [
    {
        title: "Monitoramento em tempo real",
        description: "Acompanhe todas as conversas ativas da equipe em um painel unificado.",
        Icon: MonitorHeartOutlinedIcon,
    },
    {
        title: "Auditoria completa",
        description: "Acesse o histórico completo de qualquer conversa para revisão.",
        Icon: FactCheckOutlinedIcon,
    },
    {
        title: "Relatórios detalhados",
        description: "Gere relatórios de performance, tempo de resposta e qualidade.",
        Icon: QueryStatsOutlinedIcon,
    },
    {
        title: "Integração com CRM",
        description: "Conecte com seu CRM e centralize os dados de atendimento.",
        Icon: LinkRoundedIcon,
    },
    {
        title: "Segurança e LGPD",
        description: "Dados criptografados e em conformidade com a legislação brasileira.",
        Icon: LockOutlinedIcon,
    },
    {
        title: "Alertas personalizados",
        description: "Configure gatilhos para palavras-chave, SLA e comportamentos.",
        Icon: NotificationsActiveOutlinedIcon,
    },
]

// const testimonialBrands = [
//     "Explorar",
//     "Anfitriões",
//     "Real",
//     "Hotel Bank",
//     "Ativa",
//     "GTO",
// ]

// const testimonials = [
//     {
//         quote: '"Depois do Kwik Scout, reduzimos em 40% as reclamações de atendimento. A visibilidade que temos agora é incrível."',
//         name: "Carla Mendes",
//         role: "Diretora de Operações, Hotel Bank",
//     },
//     {
//         quote: '"Finalmente consigo auditar conversas e garantir que minha equipe segue o script. Indispensável para compliance."',
//         name: "Ricardo Alves",
//         role: "CEO, Ativa Seguros",
//     },
//     {
//         quote: '"Os alertas automáticos nos ajudam a agir antes que problemas virem crises. Recomendo para qualquer gestor."',
//         name: "Fernanda Costa",
//         role: "Head de CS, GTO Logistica",
//     },
// ]

const onboardingSteps = [
    {
        step: 1,
        title: "Crie sua conta",
        description: "Cadastro super rápido em menos de 1 minuto!",
        Icon: PersonAddAlt1OutlinedIcon,
    },
    {
        step: 2,
        title: "Conecte números",
        description: "Integre os números de WhatsApp de sua equipe em poucos cliques.",
        Icon: SmartphoneOutlinedIcon,
    },
    {
        step: 3,
        title: "Acompanhe conversas",
        description: "Comece a monitorar e gerar insights imediatamente.",
        Icon: QueryStatsOutlinedIcon,
    },
]

const FAQRow = ({ question, answer }) => {
    const [expanded, setExpanded] = useState(false)
    return (
        <Box className={`landing-faq-item${expanded ? " landing-faq-item--expanded" : ""}`}>
            <button type="button" className="landing-faq-item__trigger" onClick={() => setExpanded(!expanded)}>
                <Typography component="span" className="landing-faq-item__question">{question}</Typography>
                {expanded ? (
                    <KeyboardArrowUpRoundedIcon className="landing-faq-item__icon" />
                ) : (
                    <KeyboardArrowDownRoundedIcon className="landing-faq-item__icon" />
                )}
            </button>
            {expanded && (
                <Typography component="p" className="landing-faq-item__answer">{answer}</Typography>
            )}
        </Box>
    )
}

const HeroSection = () => {
    const navigate = useNavigate()
    return (
        <section id="hero" className="hero-background landing-hero-section">
            <div className="landing-hero-inner">
                <LandingPill icon={WhatsAppIcon} className="landing-hero-pill">
                    Monitoramento de WhatsApp Corporativo com IA
                </LandingPill>

                <h1 className="landing-hero-title">
                    Monitore todas as conversas de
                    <span className="landing-hero-title-line">
                        WhatsApp da sua equipe <span className="landing-hero-title-accent">em poucos minutos</span>
                    </span>
                </h1>

                <p className="landing-hero-description">
                    Com o Kwik Scout, gestores têm visibilidade total sobre o atendimento via WhatsApp Corporativo e IA que transforma dados em relatórios inteligentes, auditoria avançada e alertas para proteger o negócio.
                </p>

                <div className="landing-hero-actions">
                    <LandingButton
                        onClick={() => navigate("/teste_agora")}
                        variant="primary"
                        size="heroPrimary"
                    >
                        Comece seu teste grátis
                        <ArrowForwardIcon className="!text-[1.15rem]" />
                    </LandingButton>
                    <LandingButton
                        onClick={() => scheduleDemo()}
                        variant="secondary"
                        size="heroSecondary"
                    >
                        Agende uma demonstração
                    </LandingButton>
                </div>

                <div className="landing-hero-benefits">
                    {heroBenefits.map((benefit) => (
                        <div key={benefit} className="landing-hero-benefit">
                            <CheckIcon fontSize="small" className="landing-hero-benefit-icon" />
                            <span>{benefit}</span>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    )
}

const VulnerabilitySection = () => (
    <section id="vulnerabilidade" className="landing-vulnerability-section landing-full-bleed-section">
        <div className="landing-vulnerability-inner">
            <h2 className="landing-section-title">
                Sem monitoramento, seu negócio fica{' '}
                <span className="landing-section-title-danger">vulnerável</span>
            </h2>
            <p className="landing-section-description landing-vulnerability-description">
                Empresas que não monitoram suas conversas de WhatsApp enfrentam riscos reais todos os dias.
            </p>
            <div className="landing-vulnerability-grid">
                {vulnerabilityItems.map((item) => {
                    return (
                        <LandingProblemCard
                            key={item.title}
                            icon={item.Icon}
                            title={item.title}
                            description={item.description}
                        />
                    )
                })}
            </div>

            <LandingButton
                onClick={() => document.getElementById("funcionalidades")?.scrollIntoView({ behavior: "smooth" })}
                variant="link"
                size="link"
                className="landing-vulnerability-link"
            >
                Saiba como o Kwik Scout resolve
                <ArrowForwardIcon className="!text-[1.1rem]" />
            </LandingButton>
        </div>
    </section>
)

const PlatformFeatures = () => {
    const navigate = useNavigate()
    return (
        <section id="funcionalidades" className="landing-platform-section">
            <div className="landing-platform-inner">
                <h2 className="landing-section-title landing-platform-title">
                    Kwik Scout: monitoria, auditoria e insights <span className="landing-platform-title-accent">em um único painel</span>
                </h2>
                <p className="landing-section-description landing-platform-description">
                    Uma plataforma completa para você ter controle total sobre as conversas de WhatsApp da sua equipe.
                </p>

                <div className="landing-platform-list">
                    {platformFeatures.map((feature) => (
                        <LandingFeatureListItem
                            key={feature.title}
                            icon={feature.Icon}
                            title={feature.title}
                            description={feature.description}
                        />
                    ))}
                </div>

                <div className="landing-platform-cta-wrap">
                    <LandingButton
                        size="section"
                        variant="primary"
                        className="landing-platform-cta"
                        onClick={() => navigate("/teste_agora")}
                    >
                        Conheça a plataforma
                    </LandingButton>
                </div>
            </div>
        </section>
    )
}

const ResultsSection = () => (
    <section id="resultados" className="landing-results-section landing-full-bleed-section">
        <div className="landing-results-inner">
            <h2 className="landing-section-title landing-results-title">
                Resultados que gestores sentem <span className="landing-platform-title-accent">no dia a dia</span>
            </h2>
            <p className="landing-section-description landing-results-description">
                Benefícios práticos que impactam diretamente a operação e os resultados do seu negócio.
            </p>

            <div className="landing-results-grid">
                {resultsItems.map((item) => (
                    <Box key={item.title} className="landing-results-card">
                        <Box className="landing-results-card__icon-wrap">
                            <item.Icon className="landing-results-card__icon" />
                        </Box>
                        <Box className="landing-results-card__content">
                            <Typography component="h3" className="landing-results-card__title">
                                {item.title}
                            </Typography>
                            <Typography component="p" className="landing-results-card__description">
                                {item.description}
                            </Typography>
                        </Box>
                    </Box>
                ))}
            </div>
        </div>
    </section>
)

const ComparisonSection = () => (
    <section id="comparacao" className="landing-comparison-section landing-full-bleed-section">
        <div className="landing-comparison-inner">
            <h2 className="landing-section-title landing-comparison-title">
                Feito sob medida para <span className="landing-platform-title-accent">a sua operação</span>
            </h2>

            <div className="landing-comparison-grid">
                {comparisonColumns.map((column) => (
                    <Box
                        key={column.title}
                        className={`landing-comparison-card landing-comparison-card--${column.tone}`}
                    >
                        <Typography component="h3" className={`landing-comparison-card__title landing-comparison-card__title--${column.tone}`}>
                            {column.title}
                        </Typography>

                        <div className={`landing-comparison-card__divider landing-comparison-card__divider--${column.tone}`} />

                        <div className="landing-comparison-card__list">
                            {column.items.map((item) => (
                                <div key={item} className="landing-comparison-card__item">
                                    <column.Icon className={`landing-comparison-card__item-icon landing-comparison-card__item-icon--${column.tone}`} />
                                    <Typography component="p" className="landing-comparison-card__item-text">
                                        {item}
                                    </Typography>
                                </div>
                            ))}
                        </div>
                    </Box>
                ))}
            </div>
        </div>
    </section>
)

const FeaturesGrid = () => {
    const navigate = useNavigate()
    return (
        <section id="tudo-que-voce-precisa" className="landing-feature-grid-section landing-full-bleed-section">
            <div className="landing-feature-grid-inner">
                <h2 className="landing-section-title landing-feature-grid-title">
                    Tudo que você precisa para monitorar o <span className="landing-platform-title-accent">WhatsApp</span>
                </h2>
                <p className="landing-section-description landing-feature-grid-description">
                    Funcionalidades completas para garantir visibilidade, controle e segurança no atendimento.
                </p>

                <div className="landing-feature-grid-cards">
                    {featureCards.map((feature) => (
                        <Box key={feature.title} className="landing-feature-grid-card">
                            <Box className="landing-feature-grid-card__icon-wrap">
                                <feature.Icon className="landing-feature-grid-card__icon" />
                            </Box>
                            <Typography component="h3" className="landing-feature-grid-card__title">
                                {feature.title}
                            </Typography>
                            <Typography component="p" className="landing-feature-grid-card__description">
                                {feature.description}
                            </Typography>
                        </Box>
                    ))}
                </div>

                <div className="landing-feature-grid-cta-wrap">
                    <LandingButton
                        size="section"
                        variant="primary"
                        className="landing-feature-grid-cta"
                        onClick={() => navigate("/teste_agora")}
                    >
                        Comece agora mesmo seu teste grátis
                    </LandingButton>
                </div>
            </div>
        </section>
    )
}

const TestimonialsSection = () => (
    <section id="depoimentos" className="landing-testimonials-section landing-full-bleed-section">
        <div className="landing-testimonials-inner">
            <h2 className="landing-section-title landing-testimonials-title">
                Quem já confia no <span className="landing-platform-title-accent">Kwik Scout</span>
            </h2>
            <p className="landing-section-description landing-testimonials-description">
                Empresas de diversos setores já transformaram a gestão de WhatsApp de suas equipes com nossa plataforma.
            </p>

            {/*<div className="landing-testimonials-brands">
                {testimonialBrands.map((brand) => (
                    <span key={brand} className="landing-testimonials-brand">{brand}</span>
                ))}
            </div>

            <div className="landing-testimonials-grid">
                {testimonials.map((testimonial) => (
                    <Box key={testimonial.name} className="landing-testimonial-card">
                        <div className="landing-testimonial-card__stars" aria-hidden="true">
                            {Array.from({ length: 5 }).map((_, index) => (
                                <StarRoundedIcon key={`${testimonial.name}-${index}`} className="landing-testimonial-card__star" />
                            ))}
                        </div>

                        <Typography component="p" className="landing-testimonial-card__quote">
                            {testimonial.quote}
                        </Typography>

                        <Typography component="h3" className="landing-testimonial-card__name">
                            {testimonial.name}
                        </Typography>

                        <Typography component="p" className="landing-testimonial-card__role">
                            {testimonial.role}
                        </Typography>
                    </Box>
                ))}
            </div>*/}
        </div>
    </section>
)

const PricingSection = () => {
    const navigate = useNavigate()

    const [annualDiscount, setAnnualDiscount] = useState(true)
    const [landingPlans, setLandingPlans] = useState([
        {
            name: "Free",
            description: "Experimente sem compromisso",
            icon: <MoneyOffIcon />,
            price: 0,
            features: [
                "1 número monitorado",
                "Histórico de 7 dias",
                "Registra texto e áudio",
                "Dashboard analítico",
                "Busca por termos",
                "1 administrador",
                "Conformidade LGPD",
                "Infraestrutura no Brasil",
            ],
            button: "Começar teste grátis"
        },
        {
            name: "Starter",
            description: "Para pequenos times",
            icon: <GroupsIcon />,
            price: 145,
            discount: 26,
            discountBadge: "Economize R$ 26,00",
            features: [
                "Todos os recursos do Free",
                "até 5 números monitorados",
                "Histórico de 60 dias",
                "Registra todas as mídias",
                "Lista de exclusão",
                "Alertas por palavras-chave",
                "Exportação de conversas",
                "Suporte 8x5",
            ],
            button: "Começar teste grátis"
        },
        {
            name: "Growth",
            description: "Equipes em operação real",
            icon: <TrendingUpIcon />,
            price: 449,
            discount: 369,
            discountBadge: "Economize R$ 80,00",
            popular: true,
            features: [
                "Todos os recursos do Starter",
                "até 20 números monitorados",
                "Histórico de 365 dias, sem limite de espaço de armazenamento",
                "IA e insights automáticos",
                "até 2 gestores",
                "Notificações por e-mail",
                "Gestão por equipes",
                "Exportação automática",
            ],
            button: "Começar teste grátis",
            badge: "Mais Popular",
        },
        {
            name: "Enterprise",
            description: "Regulação, integração e escala",
            icon: <BusinessIcon />,
            price: null,
            features: [
                "Todos os recursos do Growth",
                "Números sob medida",
                "Histórico ilimitado *",
                "Gestores ilimitados",
                "CRM nativo",
                "Onboarding assistido",
                "Suporte dedicado",
                "Gerente de conta dedicado",
                "SSO (Single Sign-On)",
            ],
            button: "Saiba mais"
        },
    ])

    useEffect(() => {
        const loadPlans = async () => {
            const apiPlans = await listStripePlans();
            setLandingPlans((prevPlans) => {
                return prevPlans.map((plan) => {
                    const apiPlan = apiPlans.find(p => p.name.endsWith(plan.name))
                    if (!apiPlan) return plan
                    return {
                        ...plan,
                        price: apiPlan.monthly_value ?? plan.price,
                        discount: apiPlan.discount_value ?? plan.discount,
                        discountBadge: apiPlan.discount_badge ?? plan.discountBadge,
                    }
                })
            })
        }
        loadPlans()
    }, [])

    return (
        <section id="planos" className="landing-pricing-section landing-full-bleed-section">
            <div className="landing-pricing-inner">
                <h2 className="landing-section-title landing-pricing-title">
                    Planos simples para <span className="landing-platform-title-accent">começar rápido</span>
                </h2>
                <p className="landing-section-description landing-pricing-description">
                    Escolha o plano ideal para o tamanho da sua operação. Todos incluem <strong>teste grátis de 7 dias!</strong>
                </p>

                <div className="landing-pricing-switch">
                    <span>Mensal</span>
                    <Switch
                        checked={annualDiscount}
                        onChange={(event) => setAnnualDiscount(event.target.checked)}
                    />
                    <span>Anual (com desconto)</span>
                </div>

                <div className="landing-pricing-grid">
                    {landingPlans.map((plan) => {
                        const popular = plan.popular
                        return (
                            <Box key={plan.name} className={`landing-pricing-card${popular ? " landing-pricing-card--popular" : ""}`}>
                                {plan.badge && (
                                    <div className="landing-pricing-card__badge">
                                        {plan.badge}
                                    </div>
                                )}
                                
                                <Typography component="h3" className="landing-pricing-card__name">
                                    {plan.icon}
                                    {plan.name}
                                    {annualDiscount && plan.discountBadge && (
                                        <Tooltip title={plan.discountBadge}>
                                            <DiscountIcon fontSize="small" color="info" />
                                        </Tooltip>
                                    )}
                                </Typography>

                                <Typography component="h6" className="landing-pricing-card__description">
                                    {plan.description}
                                </Typography>

                                <div className="landing-pricing-card__price-row">
                                    <Typography component="span" className="landing-pricing-card__price">
                                        {plan.price !== null
                                            ? (annualDiscount && plan.discount)
                                                ? `R$ ${plan.price - plan.discount}`
                                                : `R$ ${plan.price}`
                                            : "Sob consulta"
                                        }
                                    </Typography>
                                    {plan.price !== null && (
                                        <Typography component="span" className="landing-pricing-card__price-suffix">/mês</Typography>
                                    )}
                                </div>

                                <div className="landing-pricing-card__features">
                                    {plan.features.map((feature) => (
                                        <div key={feature} className="landing-pricing-card__feature">
                                            <span className="compare-yes">✔</span>
                                            <Typography component="span" className="landing-pricing-card__feature-text">
                                                {feature}
                                            </Typography>
                                        </div>
                                    ))}
                                </div>

                                <div className="landing-pricing-card__cta-wrap">
                                    <LandingButton
                                        size="section"
                                        variant={popular ? "primary" : "secondary"}
                                        className={`landing-pricing-card__cta${popular ? " landing-pricing-card__cta--popular" : ""}`}
                                        onClick={() => navigate("/teste_agora")}
                                    >
                                        {plan.button}
                                    </LandingButton>
                                </div>
                            </Box>
                        )
                    })}
                </div>
                <div style={{ marginTop: "1rem", color: "grey", fontSize: "0.9rem" }}>* Durante a vigência do contrato.</div>
            </div>
        </section>
    )
}

const comparePlans = ["free", "starter", "growth", "enterprise"]

const compareFeatures = [
    {
        label: "Período de armazenamento do histórico das conversas",
        values: {
            free: "7 dias",
            starter: "60 dias",
            growth: "365 dias",
            enterprise: "ilimitado *",
        }
    },
    {
        label: "Quantidade máxima de gestores",
        values: {
            free: "1",
            starter: "1",
            growth: "2",
            enterprise: "sob demanda",
        }
    },
    {
        label: "Quantidade máxima de números monitorados",
        values: {
            free: "1",
            starter: "5",
            growth: "20",
            enterprise: "sob demanda",
        }
    },
    {
        label: "Tipos de registros nas conversas",
        values: {
            free: "texto e áudio",
            starter: "todas",
            growth: "todas",
            enterprise: "todas",
        }
    },
    {
        label: "Conformidade LGPD",
        values: {
            free: true,
            starter: true,
            growth: true,
            enterprise: true,
        }
    },
    {
        label: "Infraestrutura no Brasil",
        values: {
            free: true,
            starter: true,
            growth: true,
            enterprise: true,
        }
    },
    {
        label: "Busca por termos em todas as conversas",
        values: {
            free: true,
            starter: true,
            growth: true,
            enterprise: true,
        }
    },
    {
        label: "Dashboard analítico",
        values: {
            free: true,
            starter: true,
            growth: true,
            enterprise: true,
        }
    },
    {
        label: "Exportação de conversas ou grupos",
        values: {
            free: false,
            starter: "manual",
            growth: "automática",
            enterprise: "automática",
        }
    },
    {
        label: "Suporte",
        values: {
            free: false,
            starter: "8x5",
            growth: "8x5",
            enterprise: "dedicado",
        }
    },
    {
        label: "Lista de exclusão de contatos em número monitorados",
        values: {
            free: false,
            starter: true,
            growth: true,
            enterprise: true,
        }
    },
    {
        label: "Alertas por palavras-chave nas conversas",
        values: {
            free: false,
            starter: true,
            growth: true,
            enterprise: true,
        }
    },
    {
        label: "Notificações por e-mail",
        values: {
            free: false,
            starter: false,
            growth: true,
            enterprise: true,
        }
    },
    {
        label: "IA e insights automáticos",
        values: {
            free: false,
            starter: false,
            growth: true,
            enterprise: true,
        }
    },
    {
        label: "Gestão por equipes",
        values: {
            free: false,
            starter: false,
            growth: true,
            enterprise: true,
        }
    },
    {
        label: "CRM nativo",
        values: {
            free: false,
            starter: false,
            growth: false,
            enterprise: true,
        }
    },
    {
        label: "Gerente de conta dedicado",
        values: {
            free: false,
            starter: false,
            growth: false,
            enterprise: true,
        }
    },
    {
        label: "SSO (Single Sign-On)",
        values: {
            free: false,
            starter: false,
            growth: false,
            enterprise: true,
        }
    },
]

const renderValue = (value) => {
    if (typeof value === "boolean") {
        return value
            ? <span className="compare-yes">✔</span>
            : <span className="compare-no">
                <LockIcon />
            </span>
    }
    return value
}

const CompareSection = () => {
    const navigate = useNavigate()
    return (
        <section id="compare" className="landing-compare-section landing-full-bleed-section">
            <div className="landing-compare-inner">
                <h2 className="landing-section-title landing-compare-title">
                    Compare
                </h2>
                <p className="landing-section-description landing-compare-description">
                    Comparação detalhada entre os planos <strong>Free</strong>, <strong>Starter</strong>, <strong>Growth</strong> e <strong>Enterprise</strong>.
                </p>
                <TableContainer component={Paper} elevation={0} className="landing-compare-table">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Recurso</TableCell>
                                {comparePlans.map(plan => (
                                    <TableCell key={plan} align="center">
                                        {plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : ""}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {compareFeatures.map((feature) => (
                                <TableRow key={feature.label}>
                                    <TableCell>{feature.label}</TableCell>

                                    {comparePlans.map(plan => (
                                        <TableCell key={plan} align="center">
                                            {renderValue(feature.values[plan])}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                
                <div style={{ marginTop: "1rem", color: "grey", fontSize: "0.9rem" }}>* Durante a vigência do contrato.</div>

                <div className="landing-steps-cta-wrap">
                    <LandingButton size="section" variant="primary" className="landing-steps-cta" onClick={() => navigate("/teste_agora")}>
                        Comece agora mesmo seu teste grátis
                    </LandingButton>
                </div>
            </div>
        </section>
    )
}

const StepsSection = () => {
    const navigate = useNavigate()
    return (
        <section id="como-funciona" className="landing-steps-section landing-full-bleed-section">
            <div className="landing-steps-inner">
                <h2 className="landing-section-title landing-steps-title">
                    Começar é simples: <span className="landing-platform-title-accent">apenas 3 passos</span>
                </h2>
                <p className="landing-section-description landing-steps-description">
                    Do cadastro ao primeiro insight em poucos minutos. Sem complicação.
                </p>

                <div className="landing-steps-grid">
                    {onboardingSteps.map((item) => (
                        <Box key={item.step} className="landing-step-card">
                            <div className="landing-step-card__icon-shell">
                                <div className="landing-step-card__icon-wrap">
                                    <item.Icon className="landing-step-card__icon" />
                                </div>
                                <div className="landing-step-card__badge">{item.step}</div>
                            </div>

                            <Typography component="h3" className="landing-step-card__title">
                                {item.title}
                            </Typography>

                            <Typography component="p" className="landing-step-card__description">
                                {item.description}
                            </Typography>
                        </Box>
                    ))}
                </div>

                <div className="landing-steps-cta-wrap">
                    <LandingButton size="section" variant="primary" className="landing-steps-cta" onClick={() => navigate("/teste_agora")}>
                        Comece agora mesmo seu teste grátis
                    </LandingButton>
                </div>
            </div>
        </section>
    )
}

const FAQSection = () => (
    <section id="faq" className="landing-faq-section landing-full-bleed-section">
        <div className="landing-faq-inner">
            <h2 className="landing-section-title landing-faq-title">Perguntas frequentes</h2>

            <div className="landing-faq-list">
                {faqItems.map((item, idx) => <FAQRow key={idx} question={item.question} answer={item.answer} />)}
            </div>
        </div>
    </section>
)

const BottomCTA = () => {
    const navigate = useNavigate()
    return (
        <section className="landing-bottom-cta-section landing-full-bleed-section">
            <div className="landing-bottom-cta-inner">
                <h2 className="landing-bottom-cta-title">
                    Pronto para ter controle total das conversas no <span className="landing-bottom-cta-title-accent">WhatsApp?</span>
                </h2>

                <p className="landing-bottom-cta-description">
                    Comece seu teste grátis de 7 dias e descubra como o Kwik Scout pode transformar a gestão do atendimento da sua equipe.
                </p>

                <div className="landing-bottom-cta-actions">
                    <LandingButton
                        size="section"
                        variant="primary"
                        className="landing-bottom-cta-primary"
                        onClick={() => navigate("/teste_agora")}
                    >
                        Comece agora
                        <ArrowForwardIcon className="!text-[1.2rem]" />
                    </LandingButton>

                    <button
                        type="button"
                        className="landing-bottom-cta-secondary"
                        onClick={() => scheduleDemo()}
                    >
                        ... ou agende uma demonstração
                    </button>
                </div>
            </div>
        </section>
    )
}

const Landing = () => {
    return (
        <Box className="landing">
            <HeroSection />
            <VulnerabilitySection />
            <PlatformFeatures />
            <ResultsSection />
            <ComparisonSection />
            <FeaturesGrid />
            <TestimonialsSection />
            <PricingSection />
            <CompareSection />
            <StepsSection />
            <FAQSection />
            <BottomCTA />
            <LandingFooter />
        </Box>
    )
}

export default Landing
